import { useState } from 'react';
import { Bell } from 'lucide-react';
import { api } from '@/lib/api';
import { trackPriceAlertSubscribe } from '@/lib/analytics';

interface PriceDropAlertProps {
  slug: string;
  brand?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PriceDropAlert({ slug, brand }: PriceDropAlertProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    try {
      await api.priceAlerts.subscribe({ email, vehicleSlug: slug, brand });
      trackPriceAlertSubscribe(slug, brand);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div className="mt-2 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-2.5 text-sm text-blue-300">
        ✓ You're subscribed. We'll email you when the price drops.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="flex flex-1 items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2">
        <Bell className="h-4 w-4 flex-shrink-0 text-blue-400" aria-hidden="true" />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
          placeholder="Get price drop alert"
          aria-label="Email for price drop alert"
          className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="rounded-lg border border-blue-500/30 bg-blue-500/20 px-4 py-2 text-sm font-semibold text-blue-300 hover:bg-blue-500/30 disabled:opacity-60"
      >
        {status === 'submitting' ? 'Subscribing…' : 'Notify me'}
      </button>
      {status === 'error' && (
        <p className="text-xs text-red-400 sm:basis-full">Please enter a valid email.</p>
      )}
    </form>
  );
}
